import { useTranslation } from "react-i18next";
import BookDemoButton from "@/components/BookDemoButton";
import HeroWorkflowMockup from "@/components/HeroWorkflowMockup";

const HeroSection = () => {
  const { t } = useTranslation();
  const metrics = [
    { value: "−19%", label: t('featureResults.lowerOverruns') },
    { value: "−25%", label: t('featureResults.fewerReworks') },
    { value: t('featureResults.twentyHoursValue'), label: t('featureResults.savedPerWeek') },
    { value: "+$85k", label: t('featureResults.annualProfit') },
  ];

  return (
    <section className="relative min-h-screen overflow-hidden bg-[hsl(240,15%,8%)] pt-[72px]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_14%,rgba(59,130,246,0.16),transparent_42%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-30"
        style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage: "linear-gradient(to bottom, black, transparent 76%)",
        }}
      />

      {/* Hero content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 sm:py-10 lg:py-12">
        {/* Text content */}
        <div className="mx-auto w-full max-w-6xl space-y-3 text-center">
          <h1 className="feature-descriptor text-xs text-blue-400 sm:text-sm">
            {t('hero.descriptor')}
          </h1>
          <p className="homepage-hero-display text-3xl font-black uppercase tracking-tight text-white sm:text-4xl lg:text-[52px]">
            <span className="block">{t('hero.title')}</span>
            <span className="block text-blue-400">{t('hero.titleLine2')}</span>
          </p>

          <p className="mx-auto max-w-2xl text-base leading-relaxed text-gray-400 sm:text-xl">
            {t('hero.description')}
          </p>

          <div className="relative z-20 flex justify-center pb-2 pt-3">
            <BookDemoButton variant="hero" />
          </div>
        </div>

        <div className="mx-auto mt-8 w-full max-w-6xl">
          <HeroWorkflowMockup />
        </div>

        <div className="relative z-10 mx-auto mt-8 w-full max-w-6xl overflow-hidden rounded-2xl border border-blue-400/25 bg-[linear-gradient(135deg,hsl(221,35%,20%),hsl(240,18%,10%)_62%)] text-left shadow-[0_20px_60px_rgba(0,0,0,0.24)]">
            <div className="grid grid-cols-2 lg:grid-cols-[minmax(210px,0.9fr)_repeat(4,minmax(0,1fr))]">
              <div className="col-span-2 flex min-h-[108px] flex-col items-center justify-center gap-2 border-b border-white/15 bg-white/[0.025] px-5 py-4 text-center lg:col-span-1 lg:min-h-[148px] lg:border-b-0">
                <span className="font-sans text-base font-medium leading-snug text-white lg:text-lg">
                  {t('featureResults.label')}
                </span>
                <p className="text-[9px] leading-snug text-white/45 lg:text-[10px]">
                  {t('featureResults.customerReported')}
                </p>
              </div>

              {metrics.map((metric) => (
                <div
                  key={metric.label}
                  className="flex min-h-[108px] min-w-0 flex-col items-center justify-center border-b border-l border-white/15 px-3 py-5 text-center last:border-b-0 lg:min-h-[148px] lg:border-b-0 lg:px-5"
                >
                  <div className={`homepage-metric-value whitespace-nowrap leading-none text-white ${metric.value.length > 7 ? "text-[27px] sm:text-[34px] lg:text-[44px]" : "text-[30px] sm:text-[38px] lg:text-[50px]"}`}>
                    {metric.value}
                  </div>
                  <p className="mt-3 text-xs leading-snug text-white/65 lg:whitespace-nowrap lg:text-[15px]">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
      </div>

      {/* Gradient transition to next dark section */}
      <div className="absolute inset-x-0 bottom-0 h-12 lg:h-20 bg-gradient-to-b from-transparent to-[hsl(240,15%,8%)] pointer-events-none z-0" />
    </section>
  );
};

export default HeroSection;
